<template>
<v-menu
    v-model="show_menu"
    :close-on-content-click="false"
    left
    offset-y
    max-height="450"
>
    <template v-slot:activator="{ on, attrs }">
        <slot name="activator" :on="on" :attrs="attrs">
            <v-icon
                v-bind="attrs"
                v-on="on"
                class="emoji-picker-icon"
            >
                mdi-emoticon-outline
            </v-icon>
        </slot>
    </template>

    <v-card class="emoji-picker-card">
        <v-text-field
            v-model="search_text"
            placeholder="Search emojis..."
            dense
            hide-details
            clearable
            autofocus
            class="emoji-search-field"
            prepend-inner-icon="mdi-magnify"
            @keydown.enter="selectFirstEmoji"
        />

        <div class="emoji-sections">
            <!-- Recently Used Section -->
            <div v-if="filtered_recent_emojis.length > 0" class="emoji-section">
                <div class="section-header">Recently Used</div>
                <div class="emoji-grid">
                    <v-tooltip
                        v-for="(emoji, index) in filtered_recent_emojis"
                        :key="'recent-' + index"
                        bottom
                        open-delay="300"
                    >
                        <template v-slot:activator="{ on, attrs }">
                            <span
                                class="emoji-item"
                                v-bind="attrs"
                                v-on="on"
                                @click="selectEmoji(emoji)"
                            >
                                {{ emoji.value }}
                            </span>
                        </template>
                        <span>{{ emoji.text }}</span>
                    </v-tooltip>
                </div>
            </div>

            <!-- All Emojis Section -->
            <div class="emoji-section">
                <div class="section-header">All</div>
                <div class="emoji-grid">
                    <v-tooltip
                        v-for="(emoji, index) in filtered_all_emojis"
                        :key="'all-' + index"
                        bottom
                        open-delay="300"
                    >
                        <template v-slot:activator="{ on, attrs }">
                            <span
                                class="emoji-item"
                                v-bind="attrs"
                                v-on="on"
                                @click="selectEmoji(emoji)"
                            >
                                {{ emoji.value }}
                            </span>
                        </template>
                        <span>{{ emoji.text }}</span>
                    </v-tooltip>
                    <div v-if="filtered_all_emojis.length === 0 && filtered_recent_emojis.length === 0" class="no-results">
                        No emojis found
                    </div>
                </div>
            </div>
        </div>
    </v-card>
</v-menu>
</template>

<script>
export default {
    name: 'EmojiPickerCompact',

    props: {
        // Optional: limit the number of emojis shown (increased for better browsing)
        max_results: {
            type: Number,
            default: 150
        }
    },

    data() {
        return {
            show_menu: false,
            search_text: '',
            recent_emoji_values: [],
        };
    },

    computed: {
        all_emojis() {
            return this.$rc.emojis() || [];
        },

        filtered_recent_emojis() {
            const search = (this.search_text || '').toLowerCase().trim();
            const results = [];

            for (const recent_value of this.recent_emoji_values) {
                const emoji = this.findEmojiByValue(recent_value);
                if (!emoji) continue;

                if (!search || emoji.text.toLowerCase().includes(search)) {
                    results.push(emoji);
                }
            }

            return results;
        },

        filtered_all_emojis() {
            const search = (this.search_text || '').toLowerCase().trim();
            const results = [];

            for (const emoji of this.all_emojis) {
                if (results.length >= this.max_results) break;

                if (!search || emoji.text.toLowerCase().includes(search)) {
                    results.push(emoji);
                }
            }

            return results;
        }
    },

    created() {
        this.loadRecentEmojis();
    },

    watch: {
        show_menu(val) {
            if (val) {
                this.search_text = '';
                // Reload recent emojis to stay in sync with other pickers on the page
                this.loadRecentEmojis();
            }
        }
    },

    methods: {
        loadRecentEmojis() {
            const recent = localStorage.getItem('rc_recent_emojis');
            if (!recent)
            {
                this.recent_emoji_values = [];
                return;
            }
            try
            {
                const parsed = JSON.parse(recent);
                this.recent_emoji_values = Array.isArray(parsed) ? parsed.slice(0, 10) : [];
            }
            catch (e)
            {
                this.recent_emoji_values = [];
            }
        },

        findEmojiByValue(value) {
            return this.all_emojis.find(e => e.value === value) || null;
        },

        selectEmoji(emoji) {
            this.addToRecent(emoji.value);
            this.show_menu = false;
            this.$emit('select', emoji.value);
        },

        selectFirstEmoji() {
            const first_emoji = this.filtered_recent_emojis[0] || this.filtered_all_emojis[0];
            if (first_emoji)
            {
                this.selectEmoji(first_emoji);
            }
        },

        addToRecent(value) {
            let recent = this.recent_emoji_values.slice();
            // Remove if already exists
            recent = recent.filter(v => v !== value);
            // Add to front
            recent.unshift(value);
            // Keep only last 10
            recent = recent.slice(0, 10);
            localStorage.setItem('rc_recent_emojis', JSON.stringify(recent));
            // Update reactive data
            this.recent_emoji_values = recent;
        },

        open() {
            this.show_menu = true;
        },

        close() {
            this.show_menu = false;
        }
    }
};
</script>

<style lang="scss" scoped>
.emoji-picker-icon {
    cursor: pointer;
    
    &:hover {
        color: var(--v-primary-base, #1976d2);
    }
}

.emoji-picker-card {
    max-width: 100vw;
}

.emoji-search-field {
    padding: 12px 12px 8px;
}

.emoji-sections {
    max-height: 340px;
    overflow-y: auto;
}

.emoji-section {
    &:not(:last-child) {
        margin-bottom: 2px;
    }
}

.section-header {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    color: rgba(0, 0, 0, 0.5);
    padding: 8px 12px 4px;
    letter-spacing: 0.5px;

    .emoji-section:not(:first-child) & {
        padding-top: 4px;
    }
}

.emoji-grid {
    display: grid;
    grid-template-columns: repeat(10, 1fr);
    gap: 2px;
    padding: 0 12px 4px;
}

.emoji-item {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 26px;
    width: 38px;
    height: 38px;
    cursor: pointer;
    border-radius: 4px;
    transition: background-color 0.15s;

    &:hover {
        background-color: rgba(0, 0, 0, 0.08);
    }
}

.no-results {
    grid-column: 1 / -1;
    text-align: center;
    color: rgba(0, 0, 0, 0.6);
    padding: 16px;
    font-size: 14px;
}
</style>
